import React, { useEffect, useRef, useState } from 'react';
import './agente456anim.css';

/* Hero "Agente 456 al lavoro" per il dialog Operazione in corso.
   Reattivo alla fase reale del job:
   avvio (compone 4·5·6) · registrazione (risposta automatica, waveform) ·
   trascrizione (typing "Analisi trascrizione") · analisi (Risultato che esce dal centro).
   attesa/standby -> solo il core che pulsa. Colori a token (--accent). */

const TYPED = 'Analisi trascrizione';
const PHASE_IDX = { avvio: 0, attesa: 0, standby: 0, registrazione: 1, trascrizione: 2, analisi: 3 };

function CoreIcon({ p }) {
  const c = { width: 36, height: 36, color: 'var(--accent)' };
  if (p === 1) return (  // microfono
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={c}>
      <rect x="9" y="2" width="6" height="11" rx="3" fill="currentColor" stroke="none" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8.5 21h7" />
    </svg>);
  if (p === 2) return (  // documento
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={c}>
      <path d="M7 3h8l4 4v14H7z" /><path d="M9.5 10h5M9.5 13.5h5M9.5 17h3" />
    </svg>);
  if (p === 3) return (  // lente (risultato)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={c}>
      <circle cx="11" cy="11" r="6.4" /><path d="M20 20l-3.7-3.7" /><path d="M8.6 11h4.8M11 8.6v4.8" />
    </svg>);
  return (  // cornetta (avvio/attesa/standby)
    <svg viewBox="0 0 24 24" style={c}>
      <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>);
}

export default function Agente456Anim({ fase, ultimo }) {
  const p = PHASE_IDX[fase] ?? 0;
  const [dial, setDial] = useState(0);
  const [typed, setTyped] = useState('');
  const [pop, setPop] = useState(false);
  const cv = useRef(null);
  const pRef = useRef(p); pRef.current = p;

  // avvio: compone 4·5·6
  useEffect(() => {
    if (fase !== 'avvio') { setDial(0); return undefined; }
    setDial(0); let n = 0;
    const id = setInterval(() => { n += 1; setDial(n); if (n >= 3) clearInterval(id); }, 420);
    return () => clearInterval(id);
  }, [fase]);

  // trascrizione: typing
  useEffect(() => {
    if (fase !== 'trascrizione') { setTyped(''); return undefined; }
    let n = 0;
    const id = setInterval(() => { n += 1; setTyped(TYPED.slice(0, n)); if (n >= TYPED.length) clearInterval(id); }, 42);
    return () => clearInterval(id);
  }, [fase]);

  // analisi: il risultato esce dal centro
  useEffect(() => {
    if (fase !== 'analisi') { setPop(false); return undefined; }
    const id = setTimeout(() => setPop(true), 550);
    return () => clearTimeout(id);
  }, [fase]);

  // waveform (canvas, 60fps) — viva in registrazione, quasi piatta altrove
  useEffect(() => {
    const c = cv.current; if (!c) return undefined;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const ctx = c.getContext('2d'); let raf, t0;
    const draw = (ts) => {
      if (!t0) t0 = ts; const t = (ts - t0) / 1000;
      ctx.clearRect(0, 0, c.width, c.height);
      const col = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#FF9500';
      const n = 26, gap = c.width / n, live = pRef.current === 1 ? 1 : 0.12;
      for (let i = 0; i < n; i += 1) {
        const seed = Math.sin(i * 12.9898) * 43758.5453;
        const base = 0.25 + (seed - Math.floor(seed)) * 0.5;
        const wob = 0.5 + 0.5 * Math.sin(t * 7 + i * 0.7) * Math.sin(t * 3.1 + i);
        const h = Math.max(3, (base * 0.4 + wob * 0.6 * base) * c.height * live);
        ctx.fillStyle = col; ctx.globalAlpha = 0.5 + 0.5 * wob;
        const x = i * gap + gap * 0.25, w = gap * 0.5, y = (c.height - h) / 2;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x, y, w, h, 3); else ctx.rect(x, y, w, h);
        ctx.fill();
      }
      ctx.globalAlpha = 1; raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  const vc = ultimo === 'TIM' ? '#ef4444' : '#22c55e';
  const vt = ultimo === 'TIM' ? 'TIM' : ultimo === 'NON TIM' ? 'NON TIM' : 'ESITO';

  return (
    <div className={`a456${fase === 'analisi' ? ' ph3' : ''}`}>
      <div className="a456-radar">
        <div className="a456-orbit" />
        <div className="a456-ring r1" /><div className="a456-ring r2" /><div className="a456-ring r3" />
        <div className="a456-sat" />
        <div className="a456-core"><CoreIcon p={p} /></div>
      </div>
      <div className="a456-ov">
        <div className={`a456-layer${fase === 'avvio' ? ' on' : ''}`}>
          <div style={{ display: 'flex', gap: 10 }}>
            {['4', '5', '6'].map((d, i) => <div key={d} className={`a456-key${i < dial ? ' lit' : ''}`}>{d}</div>)}
          </div>
        </div>
        <div className={`a456-layer${fase === 'registrazione' ? ' on' : ''}`}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <canvas ref={cv} width="250" height="58" />
            <div className="a456-rec"><i />REC · risposta automatica in ascolto</div>
          </div>
        </div>
        <div className={`a456-layer${fase === 'trascrizione' ? ' on' : ''}`}>
          <div className="a456-type">{typed}<span className="c">▌</span></div>
        </div>
      </div>
      <div className={`a456-verdict${pop ? ' pop' : ''}`} style={{ '--vc': vc }}>
        <div className="a456-badge" style={{ '--vc': vc }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={vc} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M7 12.5l3.2 3.2L17 8.5" /></svg>
          <b>{vt}</b>
        </div>
      </div>
    </div>
  );
}
